import { useDispatch, useSelector } from "react-redux";
import EconomicActivityService from "../services/EconomicActivityService";
import useAlerts from "./useAlerts";
import {
    loadingEconomicActivities,
    loadingEconomicActivitiesDropdown,
    deactivateStateEconomicActivity,
    updateEconomicActivity,
    addEconomicActivity,
} from "../store/slices/economicActivity/economicActivitySlice";
import type { RootState, AppDispatch } from "../store/store";
import useLoading from "./useLoading";
import useModals from "./useModals";
import useApiErrorHandler from "./useApiErrorHandler";

export const useEconomicActivities = () => {
    const { show: showAlert } = useAlerts();
    const { openModal } = useModals();
    const { handle } = useApiErrorHandler();

    const dispatch: AppDispatch = useDispatch();
    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();

    const { economicActivities, economicActivitiesDropdown, economicActivitySelected } = useSelector(
        (state: RootState) => state.economicActivity
    );

    const handlerSearchByType = async ({ searchType, name }) => {
        try {
            showLoadingIndicator(); // Mostrar el modal de carga

            let result;
            switch (searchType) {
                case 'all':
                    result = await EconomicActivityService.getAll();
                    dispatch(loadingEconomicActivities(result)); // Cargar directamente
                    break;
                case 'name':
                    result = await EconomicActivityService.findByName(name);

                    // Verificamos que result tenga los campos necesarios antes de usarlos
                    const title = result?.title || "Búsqueda completada";
                    const message = result?.message || "Actividad económica encontrada.";
                    const type = result?.type || "success";

                    showAlert(type, title, message);
                    dispatch(loadingEconomicActivities(result?.data || [])); // Si `data` no existe, enviamos un array vacío
                    break;
                default:
                    throw new Error(`Tipo de búsqueda no válido: ${searchType}`);
            }

            console.log("Resultado de la búsqueda:", result);

        } catch (error: any) {
            handle(error);
        } finally {
            hideLoadingIndicator(); // Ocultar el modal de carga al finalizar la solicitud
        }
    };


    const fetchEconomicActivities = async () => {
        try {
            showLoadingIndicator();
            const response = await EconomicActivityService.getAll();
            dispatch(loadingEconomicActivities(response));
        } catch (error: any) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };

    const fetchEconomicActivitiesDropdown = async (query: string = '') => {
        try {
            const response = await EconomicActivityService.getDropdown({ query });
            dispatch(loadingEconomicActivitiesDropdown(response));
        } catch (error: any) {
            handle(error);
        }
    };

    /**
     * 🔄 Recargar lista de Actividades Económicas (Dropdown)
     * ------------------------------------------------------
     * Obtiene **todas** las actividades económicas activas desde el backend
     * sin aplicar filtros de búsqueda. Este método se utiliza principalmente
     * después de crear o editar una actividad económica, para actualizar el
     * dropdown correspondiente sin depender de consultas textuales.
     *
     * Flujo del método:
     *  - Llama al servicio económico (`getAllDropdown`)
     *  - Desestructura el `statusCode` y el `data` (CustomResponse)
     *  - Si la respuesta es exitosa (200), actualiza el estado global (Redux)
     *  - Si no es exitosa, muestra un mensaje de advertencia
     *  - Maneja errores bajo patrón centralizado (`handle`)
     *
     * Librerías involucradas:
     *  - Redux → dispatch(loadingEconomicActivitiesDropdown)
     *  - Alertas globales → showAlert()
     *
     * Casos de uso típicos:
     *  - QuickAddEconomicActivity (creación rápida)
     *  - Edición de actividad económica
     *  - Refresco manual por el usuario
     *
     * Seguridad:
     *  - Manejo controlado de excepciones
     *  - No muta estado local directamente
     *
     * @async
     * @return {Promise<void>} No retorna información directamente;
     *                         actualiza estado global en su lugar.
     */
    const reloadEconomicActivitiesDropdown = async () => {
        try {
            const response = await EconomicActivityService.getAllDropdown();

            const { statusCode, data } = response;

            if (statusCode === 200) {
                dispatch(loadingEconomicActivitiesDropdown(data));
            } else {
                showAlert("warning", "No actualizado", "No se pudo recargar la lista.");
            }
        } catch (error: any) {
            handle(error);
        }
    };


    const handlerAddEconomicActivity = async (data: { id?: number; name: string; code: string }) => {
        try {
            if (data.id === 0) {
                const { id, ...activityData } = data;
                const response = await EconomicActivityService.save(activityData);
                const { title, message, type, data: newActivity } = response;

                if (newActivity) {
                    dispatch(addEconomicActivity(newActivity)); // ✅ Agregar la nueva actividad económica a Redux
                }

                openModal(type, { title, message, });
            } else {
                const response = await EconomicActivityService.update(data);
                const { type, title, message, data: updatedActivity } = response;

                if (updatedActivity) {
                    dispatch(updateEconomicActivity(updatedActivity)); // ✅ Actualizar la actividad económica en Redux
                }
                openModal(type, { title, message, });
            }
        } catch (error: any) {
            handle(error);
        }
    };


    const toggleEconomicActivityStatus = async (id: number) => {
        try {
            const response = await EconomicActivityService.deactivate(id);

            // ✅ Asegurar que response tenga datos antes de acceder a ellos
            const title = response?.title ?? "Operación completada";
            const message = response?.message ?? "El estado de la actividad económica ha sido actualizado.";
            const type = response?.type ?? "success";

            showAlert(type, title, message);

            // ✅ Asegurar que response.data es válido antes de actualizar el estado
            dispatch(deactivateStateEconomicActivity(response?.data ?? {}));
        } catch (error: any) {
            handle(error);
        }
    };

    const createEconomicActivity = async (data: { name: string }) => {
        try {
            const response = await EconomicActivityService.save(data);
            const { title, message, type, data: newActivity } = response;

            if (newActivity) {
                dispatch(addEconomicActivity(newActivity)); // ✅ Agregar a Redux
            }

            showAlert(type, title, message);
        } catch (error: any) {
            handle(error);
        }
    };



    return {
        handlerSearchByType,
        economicActivities,
        economicActivitiesDropdown,
        economicActivitySelected,
        fetchEconomicActivities,
        fetchEconomicActivitiesDropdown,
        reloadEconomicActivitiesDropdown,
        handlerAddEconomicActivity,
        createEconomicActivity,
        toggleEconomicActivityStatus,
    };
};
